import { ChevronDown, ChevronRight, Folder, Tag } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

export interface ProviderLabelOption {
  providerLabelId: string;
  name: string;
}

interface GmailLabelSelectorProps {
  labels: ProviderLabelOption[];
  selectedProviderLabelId?: string;
  onSelect: (label: ProviderLabelOption) => void;
  disabled?: boolean;
}

interface LabelNode {
  name: string;
  fullName: string;
  providerLabelId?: string;
  children: LabelNode[];
}

function buildTree(labels: ProviderLabelOption[]): LabelNode[] {
  const rootNodes: LabelNode[] = [];

  for (const label of labels) {
    const parts = label.name.split('/');
    let currentLevel = rootNodes;
    let path = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      path = path ? `${path}/${part}` : part;
      const isLeaf = i === parts.length - 1;

      let existingNode = currentLevel.find((node) => node.name === part);

      if (!existingNode) {
        existingNode = {
          name: part,
          fullName: path,
          children: [],
        };
        currentLevel.push(existingNode);
      }

      if (isLeaf) {
        existingNode.providerLabelId = label.providerLabelId;
      }

      currentLevel = existingNode.children;
    }
  }

  const sortNodes = (nodes: LabelNode[]) => {
    nodes.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    );
    for (const node of nodes) {
      sortNodes(node.children);
    }
  };
  sortNodes(rootNodes);

  return rootNodes;
}

/**
 * Picks one Gmail label to project an internal mail tag onto. Gmail labels are
 * additive at the apply layer, but a tag has at most one projection per
 * account (unique(email_tag_id, account_id)) — so the picker itself is a
 * single-select. Shows nested labels in an interactive tree view.
 */
export function GmailLabelSelector({
  labels,
  selectedProviderLabelId,
  onSelect,
  disabled = false,
}: GmailLabelSelectorProps) {
  const [collapsedPaths, setCollapsedPaths] = React.useState<Set<string>>(new Set());

  if (labels.length === 0) {
    return <p className="text-xs text-muted-foreground">No Gmail labels found for this account.</p>;
  }

  const tree = buildTree(labels);

  const toggleCollapse = (path: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const renderNode = (node: LabelNode, depth: number = 0) => {
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsedPaths.has(node.fullName);
    const isSelectable = !!node.providerLabelId;
    const isSelected = node.providerLabelId === selectedProviderLabelId;

    return (
      <div key={node.fullName} className="space-y-1">
        <div
          className={cn(
            'flex items-center gap-1.5 py-0.5 px-1 rounded-md text-xs transition-all hover:bg-muted/20',
            depth > 0 && 'ml-4 border-l border-border/40 pl-2'
          )}
        >
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => toggleCollapse(node.fullName, e)}
                className="p-0.5 rounded hover:bg-muted text-muted-foreground transition-all"
                disabled={disabled}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            ) : null}
          </div>

          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            {hasChildren ? (
              <Folder className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            ) : (
              <Tag className="h-3.5 w-3.5 text-muted-foreground/80 shrink-0" />
            )}

            {isSelectable ? (
              <button
                type="button"
                disabled={disabled}
                onClick={() =>
                  onSelect({
                    providerLabelId: node.providerLabelId!,
                    name: node.fullName,
                  })
                }
                className={cn(
                  'px-2 py-0.5 rounded text-[11px] font-medium border text-left truncate transition-all',
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary '
                    : 'bg-muted/30 border-transparent hover:border-primary/30',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                {node.name}
              </button>
            ) : (
              <span className="text-[11px] text-muted-foreground font-medium truncate py-0.5">
                {node.name}
              </span>
            )}
          </div>
        </div>

        {hasChildren && !isCollapsed && (
          <div className="space-y-1">
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border border-border/50 rounded-lg p-3 bg-muted/10 space-y-1 max-h-[300px] overflow-y-auto w-full">
      {tree.map((node) => renderNode(node, 0))}
    </div>
  );
}
